import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import AccountSection, {
  AccountSectionProps,
} from '../AccountSection/AccountSection'
import { TextInputProps, TextInputStandalone } from '../Form'
import SelectInput, { SelectInputProps } from '../Form/SelectInput/SelectInput'
import Link from '../Link/Link'
import WindowedPagination from '../WindowedPagination/WindowedPagination'

type ClickEvent = MouseEvent<HTMLAnchorElement>

export interface AccountAddress {
  address1: string | null
  address2: string | null
  attentionName: string | null
  city: string | null
  companyName: string | null
  countryCode: string | null
  facility: {
    id: string
    name: string
    iconUrl: string | null
  } | null
  firstName: string | null
  id: string
  lastName: string | null
  phoneNumber: string | null
  state: string | null
  zip: string | null
}

export interface AccountAddressBookProps {
  /**
   * List of address book entries
   */
  addresses: AccountAddress[]
  /**
   * Click add handler
   */
  onClickAdd?: (event: ClickEvent) => void
  /**
   * Click book from handler
   */
  onClickBookFrom?: (address: AccountAddress) => (event: ClickEvent) => void
  /**
   * Click book to handler
   */
  onClickBookTo?: (address: AccountAddress) => (event: ClickEvent) => void
  /**
   * Click delete handler
   */
  onClickDelete?: (address: AccountAddress) => (event: ClickEvent) => void
  /**
   * Click view handler
   */
  onClickView?: (address: AccountAddress) => (event: ClickEvent) => void
}

export const AccountAddressBook = ({
  addresses,
  onClickAdd,
  onClickBookFrom,
  onClickBookTo,
  onClickDelete,
  onClickView,
}: AccountAddressBookProps) => {
  const [filteredAddresses, setFilteredAddresses] =
    useState<AccountAddress[]>(addresses)
  const [sortColumn, setSortColumn] = useState<keyof AccountAddress>('lastName')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  const totalPages = Math.ceil(filteredAddresses.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedAddresses = filteredAddresses.slice(startIndex, endIndex)

  const { control } = useForm()
  const [searchQuery, setSearchQuery] = useState('')

  const searchSubstring = (addresses: AccountAddress[], query: string) => {
    const lowerCaseQuery = query ? query.toLowerCase() : ''

    return addresses.filter((address) =>
      Object.values(address).some(
        (value) =>
          typeof value === 'string' &&
          value.toLowerCase().includes(lowerCaseQuery),
      ),
    )
  }

  const onSearchChange = useCallback(
    (search: string) => {
      setSearchQuery(search)

      const newFilteredAddresses = searchSubstring(addresses, search)
      setFilteredAddresses(newFilteredAddresses)
      setCurrentPage(1)
    },
    [addresses],
  )

  const sortAddresses = (
    addresses: AccountAddress[],
    column: keyof AccountAddress,
  ) => {
    return [...addresses].sort((a, b) => {
      const aValue = String(a[column]).toLowerCase()
      const bValue = String(b[column]).toLowerCase()

      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    })
  }

  const handleSortChange = (value: string) => {
    if (typeof value !== 'undefined' && value !== sortColumn) {
      const selectedColumn = value as keyof AccountAddress
      setSortColumn(selectedColumn)
      const newSortedAddresses = sortAddresses(
        filteredAddresses,
        selectedColumn,
      )
      setFilteredAddresses(newSortedAddresses)
    }
  }

  const links: AccountSectionProps['link'] = [
    {
      onClick: onClickAdd,
      title: 'Add address',
    },
  ]

  const sortOptions: SelectInputProps['options'] = [
    {
      label: 'Last name',
      value: 'lastName',
    },
    {
      label: 'City',
      value: 'city',
    },
    {
      label: 'State',
      value: 'state',
    },
  ]

  useEffect(() => {
    setFilteredAddresses(addresses)
  }, [addresses])

  return (
    <AccountSection title="Address book" link={links}>
      <form
        onSubmit={(event) => event.preventDefault()}
        className="flex flex-col gap-y-4 lg:gap-y-6"
      >
        <div className="flex flex-col-reverse gap-y-4 lg:flex-row lg:w-[408px] lg:gap-x-6 lg:self-end">
          <SelectInput
            name="sortOption"
            options={sortOptions}
            control={control}
            defaultValue="lastName"
            className="[&_>div+div]:m-0 flex-1"
            onChange={(value: string | number | undefined) => {
              handleSortChange(value as string)
            }}
          />
          <TextInputStandalone
            placeholder="Search"
            trailingIcon="search"
            className="[&_.text-input-content]:m-0 flex-1"
            value={searchQuery}
            onChange={onSearchChange as TextInputProps['onChange']}
          />
        </div>
        <div className="grid grid-cols-4 lg:grid-cols-5 gap-x-2 px-4 lg:border-[1px] lg:border-neutral-400 rounded-8">
          <div className="grid col-span-full grid-cols-subgrid p-4 text-sm font-semibold mx-[-16px] lg:text-lg lg:leading-[1.3]">
            <span className="">Name</span>
            <span className="">City</span>
            <span className="">State</span>
            <span className="">Zip</span>
          </div>
          {paginatedAddresses.map((item) => (
            <div
              className="grid col-span-full grid-cols-subgrid lg:items-center p-4 border-t-[1px] border-t-neutral-400 mx-[-16px]"
              key={item.id}
            >
              <span className="hyphens-auto break-words lg:hyphens-none lg:break-normal">
                {item.attentionName ||
                  [item.firstName, item.lastName].filter(Boolean).join(' ')}
              </span>
              <span className="hyphens-auto break-words lg:hyphens-none lg:break-normal">
                {item.city}
              </span>
              <span className="hyphens-auto break-words lg:hyphens-none lg:break-normal">
                {item.state}
              </span>
              <span className="hyphens-auto break-words lg:hyphens-none lg:break-normal">
                {item.zip}
              </span>
              <div className="col-span-full flex gap-x-6 lg:col-start-[-1] lg:flex-wrap lg:justify-end lg:gap-x-4">
                <Link
                  className="text-neutral-700 visited:text-neutral-700 font-semibold [&]:text-xs [&]:p-0"
                  onClick={onClickView?.(item)}
                >
                  View
                </Link>
                <Link
                  className="text-neutral-700 visited:text-neutral-700 font-semibold [&]:text-xs [&]:p-0"
                  onClick={onClickDelete?.(item)}
                >
                  Delete
                </Link>
                <Link
                  className="text-neutral-700 visited:text-neutral-700 font-semibold [&]:text-xs [&]:p-0"
                  onClick={onClickBookTo?.(item)}
                >
                  Book to
                </Link>
                <Link
                  className="text-neutral-700 visited:text-neutral-700 font-semibold [&]:text-xs [&]:p-0"
                  onClick={onClickBookFrom?.(item)}
                >
                  Book from
                </Link>
              </div>
            </div>
          ))}
        </div>
        <WindowedPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </form>
    </AccountSection>
  )
}
